/* SessionsPanel — the operator's view of an account's OnlyFans sessions.
 *
 * An account can carry 1-3 logins: a Primary (slot 1, owns the realtime socket)
 * plus 1-2 REST-only Boosters (slots 2-3) that multiply the REST budget and
 * relieve 429s. The primary's state comes from is_logged_in; booster health is
 * the gateway-owned live/dead ledger (of_booster_health). Cooldowns are NOT
 * shown — they live in-memory in the gateway, never in the DB.
 *
 * Read-only display + launch controls (Add / Re-login a booster). Booster
 * launches stay disabled until they are wired end-to-end (booster_launch_enabled),
 * so this can ship ahead of the brain/Keeper side without risking slot 1.
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@heroui/react';
import { api } from '../../api/client';
import type { AccountSessions, SessionSlot } from '../../api/client';
import { FeatherIcon } from '../FeatherIcon';
import { LoadingBlock } from '../Spinner';
import { BoosterLoginModal } from './BoosterLoginModal';

type Tone = 'live' | 'dead' | 'off' | 'throttled';

function Chip({ tone, label }: { tone: Tone; label: string }) {
  const styles: Record<Tone, { bg: string; fg: string; dot: string }> = {
    live: { bg: 'rgba(35,195,160,0.14)', fg: '#2fd6ac', dot: '#2fd6ac' },
    dead: { bg: 'rgba(248,113,113,0.14)', fg: '#f87171', dot: '#f87171' },
    off: { bg: 'color-mix(in srgb, var(--om-ink) 6%, transparent)', fg: 'var(--color-text-secondary)', dot: 'var(--color-text-secondary)' },
    // Amber — throttled is distinct from dead/logged-out (the account is still
    // logged in, just rate-limited into silence right now).
    throttled: { bg: 'rgba(245,165,36,0.14)', fg: '#f5a524', dot: '#f5a524' },
  };
  const s = styles[tone];
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 10px',
        borderRadius: 999, background: s.bg, color: s.fg, fontSize: 12, fontWeight: 700,
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: 999, background: s.dot }} />
      {label}
    </span>
  );
}

function SlotRow({
  slot,
  primaryLoggedIn,
  launchEnabled,
  onRelogin,
}: {
  slot: SessionSlot;
  primaryLoggedIn: boolean;
  launchEnabled: boolean;
  onRelogin: (slot: number) => void;
}) {
  const label = slot.is_primary ? 'Primary' : `Login Session ${slot.slot}`;

  // Primary: state from is_logged_in. Booster: from the gateway health ledger.
  let chip: { tone: Tone; text: string };
  let dead = false;
  if (!slot.present) {
    chip = { tone: 'off', text: 'Empty' };
  } else if (slot.is_primary) {
    chip = primaryLoggedIn ? { tone: 'live', text: 'Live' } : { tone: 'dead', text: 'Needs re-login' };
  } else if (slot.health === 'dead') {
    chip = { tone: 'dead', text: 'Dead' };
    dead = true;
  } else {
    chip = { tone: 'live', text: 'Live' };
  }

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
        borderRadius: 10, background: 'var(--color-bg-input)',
        border: '1px solid var(--color-border-default)',
        opacity: slot.present ? 1 : 0.6,
      }}
    >
      <FeatherIcon name={slot.is_primary ? 'radio' : 'zap'} size={16} />
      <span style={{ fontWeight: 700, color: 'var(--color-text-primary)', minWidth: 78 }}>{label}</span>
      <Chip tone={chip.tone} label={chip.text} />
      {slot.present && slot.proxy && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--color-text-secondary)' }}>
          <FeatherIcon name={slot.proxy === 'own' ? 'globe' : 'link'} size={13} />
          {slot.proxy === 'own' ? 'Own IP' : 'Shared IP'}
        </span>
      )}
      <div style={{ flex: 1 }} />
      {!slot.is_primary && dead && launchEnabled && (
        <Button
          size="sm"
          variant="outline"
          onPress={() => onRelogin(slot.slot)}
          className="text-[var(--color-text-secondary)] border-[var(--color-border-default)]"
        >
          <FeatherIcon name="refresh-cw" size={14} />
          Re-login
        </Button>
      )}
    </div>
  );
}

// Shown when the account is Throttled: rate-limited into silence right now
// (every session cooling at once). Amber — deliberately NOT the red "logged out"
// tone, since a throttled account is still logged in. The recommendation is the
// two-case ladder (docs/adr/0009): a free slot -> add a session; all slots busy
// -> change a session's proxy onto a fresh IP.
function ThrottleNotice({ slotFree }: { slotFree: boolean }) {
  const AMBER = '#f5a524';
  return (
    <div
      style={{
        display: 'flex', gap: 10, padding: '10px 12px', borderRadius: 10,
        background: 'rgba(245,165,36,0.12)', border: '1px solid rgba(245,165,36,0.35)',
      }}
    >
      <span style={{ color: AMBER, display: 'flex', alignItems: 'flex-start', paddingTop: 1 }}>
        <FeatherIcon name="alert-triangle" size={16} />
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontWeight: 700, color: AMBER }}>Rate-limited right now</span>
        <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
          Every session is throttled, so the AI can’t reply until it clears.{' '}
          {slotFree
            ? 'A login slot is free — add another login session below to spread the load onto a fresh IP.'
            : 'All sessions are busy — change a session’s proxy to move it onto a fresh IP.'}
        </span>
      </div>
    </div>
  );
}

export function SessionsPanel({ accountId }: { accountId: string }) {
  const [data, setData] = useState<AccountSessions | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalSlot, setModalSlot] = useState<number | 'new' | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setData(await api.getAccountSessions(accountId));
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !data) {
    return <LoadingBlock pad="24px 0" size={18} color="var(--color-text-secondary)" label="Loading sessions…" style={{ fontSize: 13 }} />;
  }
  if (!data) return null;

  const launchEnabled = data.booster_launch_enabled;
  const canAdd = data.can_add_booster && data.primary.is_logged_in;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 700, color: 'var(--color-text-primary)' }}>Sessions</span>
        <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
          One primary plus up to two extra login sessions.
        </span>
        {data.throttled && <Chip tone="throttled" label="Throttled" />}
      </div>

      {data.throttled && <ThrottleNotice slotFree={data.next_free_slot != null} />}

      {data.slots.map((s) => (
        <SlotRow
          key={s.slot}
          slot={s}
          primaryLoggedIn={data.primary.is_logged_in}
          launchEnabled={launchEnabled}
          onRelogin={(slot) => setModalSlot(slot)}
        />
      ))}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
        <Button
          size="sm"
          variant="outline"
          isDisabled={!launchEnabled || !canAdd}
          onPress={() => setModalSlot('new')}
          className="text-[var(--color-text-secondary)] border-[var(--color-border-default)]"
        >
          <FeatherIcon name="plus" size={14} />
          Add login session
        </Button>
        {!launchEnabled && (
          <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Extra login sessions coming soon.</span>
        )}
        {launchEnabled && !canAdd && data.can_add_booster && !data.primary.is_logged_in && (
          <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Primary must be live first.</span>
        )}
      </div>

      {modalSlot !== null && (
        <BoosterLoginModal
          accountId={accountId}
          slot={modalSlot === 'new' ? undefined : modalSlot}
          onClose={() => setModalSlot(null)}
          onConnected={() => {
            setModalSlot(null);
            void load();
          }}
        />
      )}
    </div>
  );
}
